import React from "react";

import PlusIcon from "@uui/PlusCircle";

import type { SxProps } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import styled from "@mui/material/styles/styled";

import Button from "@rui/Button";
import TextField from "@rui/TextField";

const DEFAULT_PLACEHOLDER = "Paste meeting URL to add Read";

interface AddReadInputProps {
  error: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  noStartAdornment?: boolean;
  onBlur?: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  placeholder?: string;
  sx?: SxProps;
  value: string;
}

const TextInput = styled(TextField)(({ theme }) => ({
  input: {
    padding: theme.spacing(1.5),
  },
  "& .MuiInputBase-adornedEnd": {
    paddingRight: "3px",
    input: {
      paddingRight: theme.spacing(1),
    },
  },
  "& .MuiInputAdornment-root": {
    marginLeft: 0,
  },
  "& .MuiInputBase-adornedStart input": {
    paddingLeft: 0,
  },
  "& .MuiInputAdornment-positionEnd .MuiButton-root": {
    height: "41px",
    borderRadius: "6px",
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${theme.palette.shades.primary[500]} !important`,
  },
  "&:hover": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.shades.primary[500]} !important`,
    },
  },
}));

const StartAdornment = () => (
  <InputAdornment position="start">
    <PlusIcon sx={{ fontSize: "16px", color: "shades.neutral.400" }} />
  </InputAdornment>
);

const AddButton = ({
  loading,
  onClick,
}: {
  loading: boolean;
  onClick: () => void;
}) => (
  <InputAdornment position="end">
    <Button onClick={onClick} loading={loading}>
      Add
    </Button>
  </InputAdornment>
);

const AddReadInput = React.forwardRef<HTMLInputElement, AddReadInputProps>(
  (
    {
      error,
      fullWidth,
      loading = false,
      noStartAdornment,
      onBlur,
      onChange,
      onSubmit,
      placeholder,
      sx,
      value,
    },
    ref
  ) => {
    return (
      <TextInput
        InputProps={{
          startAdornment: noStartAdornment ? undefined : <StartAdornment />,
          endAdornment: value ? (
            <AddButton loading={loading} onClick={onSubmit} />
          ) : undefined,
          autoComplete: "off",
        }}
        ref={ref}
        onBlur={onBlur}
        onChange={onChange}
        sx={sx}
        placeholder={placeholder ?? DEFAULT_PLACEHOLDER}
        fullWidth={fullWidth}
        error={error}
        value={value}
      />
    );
  }
);

AddReadInput.displayName = "AddReadInput";

export default AddReadInput;
